import { Client4, WebSocketClient } from "@mattermost/client";
import { supabase } from "@/utils/supabase";
class ChatService {
    constructor() {
        this.ws = null;
        this.userCache = new Map();
        this.mmToken = null;
        /** Последний «сырой» WebSocket каждого клиента — чтобы прервать его, пока он ещё соединяется. */
        this.rawSockets = new WeakMap();
        this.client = new Client4();
        this.mmUrl = process.env.REACT_APP_MATTERMOST_URL || "";
    }
    init(mmToken, mmUrl) {
        this.mmToken = mmToken;
        this.mmUrl = mmUrl;
        this.client.setUrl(mmUrl);
        this.client.setToken(mmToken);
    }
    connectWebSocket() {
        if (!this.mmToken || !this.mmUrl) {
            throw new Error("ChatService not initialized: call init() first");
        }
        // Прежний сокет не оставляем жить рядом с новым (повторное подключение) — WP-DSP-FIX-01
        if (this.ws)
            this.closeSocket(this.ws);
        const client = new WebSocketClient({
            newWebSocketFn: (url) => {
                const raw = new WebSocket(url);
                this.rawSockets.set(client, raw);
                return raw;
            },
        });
        this.ws = client;
        const wsUrl = this.mmUrl.replace(/^http/, "ws") + "/api/v4/websocket";
        this.ws.initialize(wsUrl, this.mmToken);
    }
    disconnect() {
        if (this.ws) {
            this.closeSocket(this.ws);
            this.ws = null;
        }
        this.userCache.clear();
        this.mmToken = null;
        // Поздние запросы прежней сессии не должны уходить с её токеном (WP-DSP-FIX-01)
        this.client.setToken("");
    }
    /**
     * `WebSocketClient.close()` закрывает только открытый сокет и снимает таймер переподключения; соединяющийся
     * сокет продолжает жить, а его `onclose` запускает цикл переподключения библиотеки заново (например, при
     * недоступном Mattermost после выхода). Такой сокет прерываем сами, сняв обработчики библиотеки (WP-DSP-FIX-01).
     */
    closeSocket(ws) {
        ws.close();
        const raw = this.rawSockets.get(ws);
        if (raw && raw.readyState === WebSocket.CONNECTING) {
            raw.onopen = null;
            raw.onmessage = null;
            raw.onerror = null;
            raw.onclose = null;
            raw.close();
        }
        // Страховка: если сокет всё же успеет открыться — закрыть сразу
        const closeWhenOpened = () => ws.close();
        ws.addFirstConnectListener(closeWhenOpened);
        ws.addReconnectListener(closeWhenOpened);
    }
    async fetchMmToken() {
        const botServiceUrl = process.env.REACT_APP_BOT_SERVICE_URL;
        if (!botServiceUrl) {
            throw new Error("REACT_APP_BOT_SERVICE_URL is not configured");
        }
        const { data: sessionData } = await supabase().auth.getSession();
        const jwt = sessionData?.session?.access_token;
        if (!jwt) {
            throw new Error("No Supabase session: user is not authenticated");
        }
        const response = await fetch(`${botServiceUrl}/chat/token`, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${jwt}`,
                "Content-Type": "application/json",
            },
        });
        if (!response.ok) {
            throw new Error(`Failed to fetch MM token: ${response.status} ${response.statusText}`);
        }
        const data = (await response.json());
        return data.token;
    }
    // --- Channels ---
    async getMyChannels(teamId) {
        return this.client.getMyChannels(teamId);
    }
    async getMyChannelMembers(teamId) {
        return this.client.getMyChannelMembers(teamId);
    }
    async getChannel(channelId) {
        return this.client.getChannel(channelId);
    }
    async getChannelByName(teamName, channelName) {
        return this.client.getChannelByNameAndTeamName(teamName, channelName);
    }
    async viewChannel(channelId) {
        await this.client.viewMyChannel(channelId);
    }
    // --- Posts ---
    async getPosts(channelId, page = 0, perPage = 60) {
        return this.client.getPosts(channelId, page, perPage);
    }
    async getPostsBefore(channelId, postId, perPage = 60) {
        return this.client.getPostsBefore(channelId, postId, 0, perPage);
    }
    async getPostThread(rootId) {
        return this.client.getPostThread(rootId);
    }
    async createPost(channelId, message, rootId, fileIds, props) {
        const post = {
            channel_id: channelId,
            message,
        };
        if (rootId) {
            post.root_id = rootId;
        }
        if (fileIds?.length) {
            post.file_ids = fileIds;
        }
        if (props) {
            post.props = props;
        }
        return this.client.createPost(post);
    }
    // --- Files ---
    async uploadFile(channelId, file) {
        const formData = new FormData();
        formData.append("files", file);
        formData.append("channel_id", channelId);
        return this.client.uploadFile(formData);
    }
    getFileUrl(fileId) {
        return this.client.getFileUrl(fileId, Date.now());
    }
    getFileThumbnailUrl(fileId) {
        return this.client.getFileThumbnailUrl(fileId, Date.now());
    }
    async fetchFileBlob(fileId, thumbnail = false) {
        const url = thumbnail
            ? this.client.getFileThumbnailUrl(fileId, Date.now())
            : this.client.getFileUrl(fileId, Date.now());
        const response = await fetch(url, {
            headers: { Authorization: `Bearer ${this.mmToken}` },
        });
        if (!response.ok) {
            throw new Error(`Failed to fetch file: ${response.status}`);
        }
        const blob = await response.blob();
        return URL.createObjectURL(blob);
    }
    // --- Users ---
    async getUser(userId) {
        const cached = this.userCache.get(userId);
        if (cached)
            return cached;
        const user = await this.client.getUser(userId);
        this.userCache.set(userId, user);
        return user;
    }
    async getMe() {
        const me = await this.client.getMe();
        this.userCache.set(me.id, me);
        return me;
    }
    // --- WebSocket ---
    onMessage(callback) {
        if (!this.ws) {
            throw new Error("WebSocket not connected: call connectWebSocket() first");
        }
        this.ws.addMessageListener(callback);
    }
    offMessage(callback) {
        if (!this.ws)
            return;
        this.ws.removeMessageListener(callback);
    }
    onClose(callback) {
        if (!this.ws) {
            throw new Error("WebSocket not connected: call connectWebSocket() first");
        }
        this.ws.addCloseListener(callback);
    }
    onReconnect(callback) {
        if (!this.ws) {
            throw new Error("WebSocket not connected: call connectWebSocket() first");
        }
        this.ws.addReconnectListener(callback);
    }
}
export const chatService = new ChatService();
